import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { getRotationAnalytics } from "@/lib/rotation-analytics.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/app/page-header";

export const Route = createFileRoute("/_authenticated/rotation-analytics")({
  component: RotationAnalyticsPage,
});

const WINDOW_OPTIONS = [
  { label: "Letzte 24 Stunden", hours: 24 },
  { label: "Letzte 7 Tage", hours: 24 * 7 },
  { label: "Letzte 30 Tage", hours: 24 * 30 },
  { label: "Letzte 90 Tage", hours: 24 * 90 },
];

function RotationAnalyticsPage() {
  const fn = useServerFn(getRotationAnalytics);
  const [hours, setHours] = useState<number>(24);
  const [campaignId, setCampaignId] = useState<string>("all");

  const { data, isLoading, error } = useQuery({
    queryKey: ["rotation-analytics", hours, campaignId],
    queryFn: () =>
      fn({
        data: {
          hours,
          campaignId: campaignId === "all" ? undefined : campaignId,
        },
      }),
  });

  const maxFrom = useMemo(
    () => Math.max(1, ...(data?.fromNames ?? []).map((r) => r.count)),
    [data],
  );
  const maxSubj = useMemo(() => Math.max(1, ...(data?.subjects ?? []).map((r) => r.count)), [data]);

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Rotations-Auswertung"
        description="Tatsächlich verwendete Absender-Namen und Betreff-Varianten je Zeitraum."
      />

      <div className="flex flex-wrap gap-3">
        <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((o) => (
              <SelectItem key={o.hours} value={String(o.hours)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={campaignId} onValueChange={setCampaignId}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Kampagne wählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kampagnen</SelectItem>
            {(data?.campaigns ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name ?? c.id.slice(0, 8)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <Card>
          <CardContent className="p-6 text-destructive">{(error as Error).message}</CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            Gesamt versendet (Status "sent"):{" "}
            <span className="tabular-nums">{data?.total ?? 0}</span>
          </CardTitle>
        </CardHeader>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Absender-Namen ({data?.fromNames.length ?? 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Lade…</div>
            ) : (data?.fromNames.length ?? 0) === 0 ? (
              <div className="text-sm text-muted-foreground">
                Keine Daten im gewählten Zeitraum.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From-Name</TableHead>
                    <TableHead className="w-[80px] text-right">Anzahl</TableHead>
                    <TableHead className="w-[80px] text-right">Anteil</TableHead>
                    <TableHead className="w-[160px]">Verteilung</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data!.fromNames.map((r) => (
                    <TableRow key={r.value}>
                      <TableCell className="font-medium">{r.value}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {data!.total ? ((r.count / data!.total) * 100).toFixed(1) : "0.0"}%
                      </TableCell>
                      <TableCell>
                        <div className="h-2 w-full rounded bg-muted">
                          <div
                            className="h-2 rounded bg-primary"
                            style={{ width: `${(r.count / maxFrom) * 100}%` }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Betreff-Varianten ({data?.subjects.length ?? 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Lade…</div>
            ) : (data?.subjects.length ?? 0) === 0 ? (
              <div className="text-sm text-muted-foreground">
                Keine Daten im gewählten Zeitraum.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Betreff</TableHead>
                    <TableHead className="w-[80px] text-right">Anzahl</TableHead>
                    <TableHead className="w-[80px] text-right">Anteil</TableHead>
                    <TableHead className="w-[160px]">Verteilung</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data!.subjects.map((r) => (
                    <TableRow key={r.value}>
                      <TableCell className="max-w-[380px] truncate" title={r.value}>
                        {r.value}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {data!.total ? ((r.count / data!.total) * 100).toFixed(1) : "0.0"}%
                      </TableCell>
                      <TableCell>
                        <div className="h-2 w-full rounded bg-muted">
                          <div
                            className="h-2 rounded bg-primary"
                            style={{ width: `${(r.count / maxSubj) * 100}%` }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
